import { request, url } from "@frontend/lib/http";

import type {
  Backup,
  BackupSource,
  Collection,
  Collector,
  Doctrine,
  LogFilters,
  LogSearch,
  Metrics,
  MetricsFilters,
  Profile,
  ProfileFilters,
  ProfileSummary,
  Realtime,
  RealtimeChannelDetail,
  RouteTrace,
  Trace,
  TraceFilters,
  TraceSummary,
  Workers,
} from "./types";

export type * from "./types";

type RequestOptions = { signal?: AbortSignal };

export const profilerApi = {
  async fetchProfiles(
    filters: ProfileFilters = {},
    options?: RequestOptions,
  ): Promise<ProfileSummary[]> {
    return request<ProfileSummary[]>({
      route: "api_profiler_profiles_list",
      query: filters,
      ...options,
    });
  },

  async fetchProfile(
    token: string,
    options?: RequestOptions,
  ): Promise<Profile> {
    return request<Profile>({
      route: "api_profiler_profiles_show",
      params: { token },
      ...options,
    });
  },

  async fetchCollector(
    token: string,
    name: string,
    options?: RequestOptions,
  ): Promise<Collector> {
    return request<Collector>({
      route: "api_profiler_profiles_collector",
      params: { token, name },
      ...options,
    });
  },

  async fetchCollection(options?: RequestOptions): Promise<Collection> {
    return request<Collection>({
      route: "api_profiler_collection_show",
      ...options,
    });
  },

  async updateCollection(enabled: boolean): Promise<Collection> {
    return request<Collection>({
      route: "api_profiler_collection_update",
      method: "PUT",
      body: { enabled },
    });
  },

  async fetchDoctrine(
    token: string,
    options?: RequestOptions,
  ): Promise<Doctrine> {
    return request<Doctrine>({
      route: "api_profiler_profiles_doctrine",
      params: { token },
      ...options,
    });
  },

  async fetchRouteTraces(
    token: string,
    options?: RequestOptions,
  ): Promise<RouteTrace[]> {
    return request<RouteTrace[]>({
      route: "api_profiler_profiles_routing",
      params: { token },
      ...options,
    });
  },

  async fetchTraceSpans(
    traceId: string,
    options?: RequestOptions,
  ): Promise<Trace> {
    return request<Trace>({
      route: "api_profiler_traces_spans",
      params: { traceId },
      ...options,
    });
  },

  async fetchTraceReport(
    traceId: string,
    options?: RequestOptions,
  ): Promise<string> {
    const response = await fetch(
      url("api_profiler_traces_report", { traceId }),
      options,
    );

    if (!response.ok) {
      throw new Error(`The trace report answered ${response.status}.`);
    }

    return response.text();
  },

  async fetchTraceSearch(
    filters: TraceFilters = {},
    options?: RequestOptions,
  ): Promise<TraceSummary[]> {
    return request<TraceSummary[]>({
      route: "api_profiler_traces_search",
      query: filters,
      ...options,
    });
  },

  async fetchLogs(
    filters: LogFilters = {},
    options?: RequestOptions,
  ): Promise<LogSearch> {
    return request<LogSearch>({
      route: "api_profiler_logs_search",
      query: filters,
      ...options,
    });
  },

  async fetchMetrics(
    filters: MetricsFilters = {},
    options?: RequestOptions,
  ): Promise<Metrics> {
    return request<Metrics>({
      route: "api_profiler_metrics",
      query: filters,
      ...options,
    });
  },

  async fetchWorkers(options?: RequestOptions): Promise<Workers> {
    return request<Workers>({
      route: "api_profiler_workers",
      ...options,
    });
  },

  async restartWorkers(host: string): Promise<void> {
    await request<void>({
      route: "api_profiler_workers_restart",
      method: "POST",
      params: { host },
    });
  },

  async restartWorker(host: string, pid: number): Promise<void> {
    await request<void>({
      route: "api_profiler_worker_restart",
      method: "POST",
      params: { host, pid },
    });
  },

  async resizeWorkerPool(
    host: string,
    transport: string,
    count: number,
  ): Promise<void> {
    await request<void>({
      route: "api_profiler_workers_resize",
      method: "PUT",
      params: { host, transport },
      body: { count },
    });
  },

  async fetchBackup(
    source: BackupSource,
    options?: RequestOptions,
  ): Promise<Backup> {
    return request<Backup>({
      route: "api_profiler_backup",
      params: { source },
      ...options,
    });
  },

  async startBackup(source: BackupSource, kind: string): Promise<void> {
    await request<void>({
      route: "api_profiler_backup_run",
      method: "POST",
      params: { source, kind },
    });
  },

  async fetchRealtime(options?: RequestOptions): Promise<Realtime> {
    return request<Realtime>({
      route: "api_profiler_realtime",
      ...options,
    });
  },

  async fetchRealtimeChannel(
    channel: string,
    options?: RequestOptions,
  ): Promise<RealtimeChannelDetail> {
    return request<RealtimeChannelDetail>({
      route: "api_profiler_realtime_channel",
      params: { channel },
      ...options,
    });
  },

  async publishRealtime(
    channel: string,
    data: Record<string, unknown>,
  ): Promise<void> {
    await request<void>({
      route: "api_profiler_realtime_publish",
      method: "POST",
      params: { channel },
      body: { data },
    });
  },

  async disconnectRealtimeUser(user: string): Promise<void> {
    await request<void>({
      route: "api_profiler_realtime_disconnect",
      method: "DELETE",
      params: { user },
    });
  },

  async unsubscribeRealtimeUser(channel: string, user: string): Promise<void> {
    await request<void>({
      route: "api_profiler_realtime_unsubscribe",
      method: "DELETE",
      params: { channel, user },
    });
  },

  async fetchLogChannels(
    range: string | undefined,
    options?: RequestOptions,
  ): Promise<string[]> {
    return request<string[]>({
      route: "api_profiler_logs_channels",
      query: { range },
      ...options,
    });
  },

  async fetchTrace(
    traceId: string,
    options?: RequestOptions,
  ): Promise<Profile> {
    return request<Profile>({
      route: "api_profiler_traces_show",
      params: { traceId },
      ...options,
    });
  },
};
